import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import logoImage from "../../assets/task/Wizkind_logo_final 1.svg";
import ioniconcchevrondown from "../../assets/header/ioniconcchevrondown.svg";
import logouticon from "../../assets/header/logout-nav.png";
import { useAuth } from "../../context/AuthContext";

type HeaderProps = {
    hideHeaderAndBottomBar?: boolean;
};

const Header = ({ hideHeaderAndBottomBar = false }: HeaderProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const selectAgencyScreen = location.pathname === "/select_agency";
    const { userInfo } = useAuth();
    const employeeID = String(userInfo?.employee_id ?? "");

    const [bodyClass, setBodyClass] = useState("");
    const [fontValue, setFontValue] = useState("A");
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isDarkMode, setIsDarkMode] = useState(
        () => localStorage.getItem("theme") === "dark"
    );
    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);

    const allowedEmployeeIDs = ["1000103", "1000014"];
    const firstName = (() => {
        try {
            const storedUserInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
            return storedUserInfo?.first_name?.charAt(0)?.toUpperCase() || "";
        } catch {
            return "";
        }
    })();

    useEffect(() => {
        document.body.classList.toggle("dark", isDarkMode);
        document.body.classList.toggle("light", !isDarkMode);
        return () => {
            document.body.classList.remove("dark", "light");
        };
    }, [isDarkMode]);

    useEffect(() => {
        setIsMobileMenuOpen(false);
        setIsProfileMenuOpen(false);
    }, [location.pathname]);

    const handleLogoutClick = () => {
        localStorage.clear();
        navigate("/");
    };

    const handleSettingClick = () => {
        setIsProfileMenuOpen(false);
        navigate("/agency_setting");
    };

    const handleProfileClick = () => {
        setIsProfileMenuOpen(false);
        navigate("/edit_employee");
    };

    const handleFontChange = (newClass: string, value: string) => {
        if (bodyClass) {
            document.body.classList.remove(bodyClass);
        }
        document.body.classList.add(newClass);
        setBodyClass(newClass);
        setFontValue(value);
        setIsProfileMenuOpen(false);
    };

    const handleToggleTheme = () => {
        setIsDarkMode((currentMode) => {
            const nextMode = !currentMode;
            localStorage.setItem("theme", nextMode ? "dark" : "light");
            return nextMode;
        });
    };

    const navigationItems = [
        {
            label: "Dashboard",
            path: "/dashboard",
            icon: (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                    <rect x="3" y="3" width="8" height="8" rx="2" />
                    <rect x="13" y="3" width="8" height="5" rx="2" />
                    <rect x="13" y="11" width="8" height="10" rx="2" />
                    <rect x="3" y="14" width="8" height="7" rx="2" />
                </svg>
            ),
        },
        {
            label: "POD Goals",
            path: "/goals_dash",
            icon: (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                    <circle cx="12" cy="12" r="9" />
                    <circle cx="12" cy="12" r="4.5" />
                    <circle cx="12" cy="12" r="1" />
                </svg>
            ),
        },
        {
            label: "Project",
            path: "/projects",
            icon: (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                </svg>
            ),
        },
        // {
        //   label: "Task",
        //   path: "/task",
        //   icon: (
        //     <svg viewBox="0 0 24 24" aria-hidden="true">
        //       <path d="M9 6h11M9 12h11M9 18h11M4 6l1 1 2-2M4 12l1 1 2-2M4 18l1 1 2-2" />
        //     </svg>
        //   ),
        // },
        {
            label: "Calendar",
            path: "/calendar_admin",
            icon: (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                    <rect x="3" y="5" width="18" height="16" rx="2" />
                    <path d="M3 10h18M8 3v4M16 3v4" />
                </svg>
            ),
        },
        {
            label: "Actions",
            path: "/actions",
            icon: (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M13 2 4 14h7l-1 8 9-12h-7z" />
                </svg>
            ),
        },
        {
            label: "Actions New",
            path: "/actions_new",
            icon: (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M13 2 4 14h7l-1 8 9-12h-7z" />
                </svg>
            ),
        },
        {
            label: "Messages",
            path: "/message",
            icon: (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M4 5h16v11H8l-4 4z" />
                </svg>
            ),
        },
        {
            label: "Agents",
            path: "/agents",
            icon: (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                    <rect x="4" y="7" width="16" height="12" rx="3" />
                    <path d="M12 3v4M9 12h.01M15 12h.01M9 16h6" />
                </svg>
            ),
        },
        // {
        //   label: "People",
        //   path: "/people",
        //   icon: (
        //     <svg viewBox="0 0 24 24" aria-hidden="true">
        //       <circle cx="9" cy="8" r="3.5" />
        //       <path d="M2.5 20a6.5 6.5 0 0 1 13 0M16 5a3 3 0 0 1 0 6M21.5 20a6 6 0 0 0-5-5.9" />
        //     </svg>
        //   ),
        // },
        // {
        //   label: "Reports",
        //   path: "/reports",
        //   icon: (
        //     <svg viewBox="0 0 24 24" aria-hidden="true">
        //       <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
        //     </svg>
        //   ),
        // },
        // {
        //   label: "Admin",
        //   path: "/admin",
        //   icon: (
        //     <svg viewBox="0 0 24 24" aria-hidden="true">
        //       <circle cx="12" cy="12" r="3" />
        //       <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9 7 7M17 17l2.1 2.1M19.1 4.9 17 7M7 17l-2.1 2.1" />
        //     </svg>
        //   ),
        // },
    ];

    return (
        <header className="cb-header wizkind-cb-header">
            <div className="cb-header-inner">
                <div className="cb-header-brand">
                    <img
                        className="cb-header-logo"
                        src={logoImage}
                        alt="CareBravo"
                    />
                </div>

                <div className="cb-header-navigation">

                    <button
                        type="button"
                        className="cb-mobile-menu-toggle"
                        onClick={() => setIsMobileMenuOpen((open) => !open)}
                        aria-label={isMobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
                        aria-expanded={isMobileMenuOpen}
                        aria-controls="cb-header-navigation-menu"
                    >
                        {isMobileMenuOpen ? (
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                                <path d="m6 6 12 12M18 6 6 18" />
                            </svg>
                        ) : (
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M3 6h18M3 12h18M3 18h18" />
                            </svg>
                        )}
                    </button>



                    <ul
                        id="cb-header-navigation-menu"
                        className={`cb-nav-menu ${isMobileMenuOpen ? "cb-nav-menu-open" : ""}`}
                    >
                        {/* <input
            type="search"
            placeholder="Search..."
            aria-label="Search"
            className="cb-header-search-responisve mq1100:block"
          /> */}



                        {navigationItems.map((item) => {
                            const isActive =
                                location.pathname === item.path ||
                                location.pathname.startsWith(`${item.path}/`);

                            return (

                                <li key={item.label}>
                                    <button
                                        type="button"
                                        className={`cb-nav-link ${isActive ? "cb-nav-link-active" : ""
                                            }`}
                                        onClick={() => {
                                            setIsMobileMenuOpen(false);
                                            navigate(item.path);
                                        }}
                                         aria-current={isActive ? "page" : undefined}
                                    >
                                        {item.icon}
                                        <span>{item.label}</span>
                                    </button>
                                </li>
                            );
                            
                        })}
                    </ul>

                    {/* <input
                        type="search"
                        placeholder="Search..."
                        aria-label="Search"
                        className="cb-header-search mq1100:hidden"
                    /> */}
                </div>

                {!hideHeaderAndBottomBar && (
                    <div className="cb-header-tools">
                        <div className="cb-font-menu">
                            <button type="button" className="cb-tool-button" aria-label="Font size options">
                                {fontValue}
                                <img src={ioniconcchevrondown} alt="" />
                            </button>
                            <div className="cb-dropdown-content">
                                <button type="button" onClick={() => handleFontChange("medium_font_dv", "A")}>A</button>
                                <button type="button" onClick={() => handleFontChange("large_font_dv", "A +")}>A +</button>
                                <button type="button" onClick={() => handleFontChange("small_font_dv", "A -")}>A -</button>
                            </div>
                        </div>

                        <button
                            type="button"
                            className={`cb-theme-toggle ${isDarkMode ? "cb-theme-toggle-on" : ""}`}
                            onClick={handleToggleTheme}
                            aria-label={`Switch to ${isDarkMode ? "light" : "dark"} mode`}
                            aria-pressed={isDarkMode}
                        >
                            <span />
                        </button>

                        {selectAgencyScreen ? (
                            <button
                                type="button"
                                className="cb-logout-button"
                                onClick={handleLogoutClick}
                            >
                                Log Out
                                <img src={logouticon} alt="" />
                            </button>
                        ) : (
                            <div className="cb-profile-wrap">
                                <button
                                    type="button"
                                    className="cb-profile-button"
                                    onClick={() => setIsProfileMenuOpen((open) => !open)}
                                    aria-expanded={isProfileMenuOpen}
                                    aria-label="Open profile menu"
                                >
                                    <span className="cb-profile-avatar">{firstName}</span>
                                    <img src={ioniconcchevrondown} alt="" />
                                </button>
                                {isProfileMenuOpen && (
                                    <div className="cb-profile-dropdown">
                                        {allowedEmployeeIDs.includes(employeeID) && (
                                            <button type="button" onClick={handleSettingClick}>Settings</button>
                                        )}
                                        <button type="button" onClick={handleProfileClick}>Profile</button>
                                        <button type="button" onClick={handleLogoutClick}>Logout</button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </header>
    );
};

export default Header;
